// Every request this client makes.
//
// One place, so that "the bearer token goes in a header" and "a 404 means nothing" are
// each written once. Nothing outside this module calls `fetch`.
//
// ---------------------------------------------------------------------------
// WHAT A REFUSAL MEANS HERE
// ---------------------------------------------------------------------------
//
// `packages/api/src/errors.ts` collapses "no such plan", "not yours", "not a participant"
// and "malformed id" into one 404 with one message, on purpose: a 403 would confirm that a
// preparing thread exists. The client honours that by not trying to be more informative
// than the server was. A 404 renders as "this plan isn't available to you" and never as
// "you don't have permission", which would put the distinction back that the server spent
// a design decision removing.
//
// ---------------------------------------------------------------------------
// THIS CLIENT ENFORCES NOTHING
// ---------------------------------------------------------------------------
//
// There is no permission check in this package. Not one. What a person may read is what
// the server returns; what they may do is what the server accepts. The client hides
// controls it was given nothing to drive — which is not the same thing, because removing
// the hiding changes what a screen looks like and changes nothing about what can happen.

import {
  decodeDecisionList,
  decodeDecisionNotes,
  decodeIssueHistory,
  decodeIssueView,
  type IssueHistoryEntry,
  type IssueView,
  decodePersonNotes,
  type DecisionNotes,
  type PersonNotes,
  decodeFileEnvelope,
  decodeFileList,
  type FileView,
  decodeDemoScenarioList,
  decodeDemoSheet,
  decodeDemoSource,
  decodeDemoStart,
  decodeMessageEnvelope,
  decodeMessagePage,
  decodePendingActionEnvelope,
  decodePendingActionList,
  decodeTopicEnvelope,
  decodeTopicList,
  decodeRecordResult,
  decodePersonList,
  decodeInvitationAddress,
  decodePlanEnvelope,
  decodePlanList,
  decodeRedemption,
  decodeReleaseNoteList,
  decodeThreadList,
  decodeVoiceTokens,
  type Decision,
  type DemoScenario,
  type DemoSource,
  type DemoStart,
  type InvitationAddress,
  type Message,
  type Invite,
  type TopicView,
  type RecordChange,
  type MessagePage,
  type PendingAction,
  type Person,
  type Plan,
  type Objective,
  type ReleaseNote,
  type Thread,
  type VoiceTokens,
  decodeObjectiveList,
  MalformedResponse,
} from './contract.js';

export type ApiErrorCode =
  | 'unauthenticated'
  | 'not_found'
  | 'invalid_request'
  | 'thread_closed'
  | 'already_in_plan'
  | 'artifact_unavailable'
  | 'voice_unavailable'
  | 'file_too_large'
  | 'files_unavailable'
  | 'refused'
  | 'internal'
  | 'unreachable';

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Present only for 409 `already_in_plan`, and only when the body carried a well-formed id. */
  readonly planId?: string;

  constructor(status: number, code: ApiErrorCode, message: string, planId?: string) {
    super(message);
    this.name = 'ApiFailure';
    this.status = status;
    this.code = code;
    if (planId !== undefined) {
      this.planId = planId;
    }
  }
}

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export type ApiClientOptions = {
  baseUrl: string;
  /** Read at call time, never captured, so a re-sign-in does not need a new client. */
  token: () => string | null;
  fetchImpl?: Fetch;
};

const ERROR_CODES: ReadonlySet<string> = new Set([
  'unauthenticated',
  'not_found',
  'invalid_request',
  'thread_closed',
  'already_in_plan',
  'artifact_unavailable',
  'voice_unavailable',
  'file_too_large',
  'files_unavailable',
  'refused',
  'internal',
]);

export class ApiClient {
  readonly baseUrl: string;
  /**
   * The transport, and only the transport.
   *
   * Public because tests build requests this package deliberately has no
   * method for — the plan-wide pending-action read, for one, which is a route the API
   * rejected and a client must not grow a helper for. Exposing the transport is not a hole
   * in the decoding rule: every response in `src/` still goes through `contract.ts`, and
   * there is nothing here that turns bytes into a value.
   */
  readonly transport: Fetch;
  readonly #token: () => string | null;
  readonly #fetch: Fetch;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl;
    this.#token = options.token;
    this.#fetch = options.fetchImpl ?? ((input, init) => fetch(input, init));
    this.transport = this.#fetch;
  }

  /** The current bearer token, read at call time. The stream needs it for its header. */
  bearer(): string | null {
    return this.#token();
  }

  /** The header every request carries. Never a query parameter — see `stream.ts`. */
  authorization(): Record<string, string> {
    const token = this.#token();
    return token === null ? {} : { authorization: `Bearer ${token}` };
  }

  async #request(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
    form?: FormData,
    keepalive?: boolean,
  ): Promise<unknown> {
    const headers: Record<string, string> = { ...this.authorization() };
    const init: RequestInit = { method, headers };
    if (keepalive === true) {
      init.keepalive = true;
    }
    if (form !== undefined) {
      // No content type: the runtime writes it with the multipart boundary.
      init.body = form;
    } else if (body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    let response: Response;
    try {
      response = await this.#fetch(`${this.baseUrl}${path}`, init);
    } catch (cause) {
      // The network, not the server. Distinguished because it is the one failure a person
      // can do something about, and because it must never be reported as a refusal.
      throw new ApiFailure(0, 'unreachable', 'could not reach the server');
    }
    const text = await response.text();
    let parsed: unknown;
    try {
      parsed = text === '' ? {} : JSON.parse(text);
    } catch {
      parsed = {};
    }
    if (!response.ok) {
      throw failureFrom(response.status, parsed);
    }
    return parsed;
  }

  listPlans(): Promise<Plan[]> {
    return this.#request('GET', '/v1/plans').then(decodePlanList);
  }

  createPlan(name: string): Promise<Plan> {
    return this.#request('POST', '/v1/plans', { name }).then(decodePlanEnvelope);
  }

  readPlan(planId: string): Promise<Plan> {
    return this.#request('GET', `/v1/plans/${encode(planId)}`).then(decodePlanEnvelope);
  }

  listThreads(planId: string): Promise<Thread[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/threads`).then(
      decodeThreadList,
    );
  }

  listPeople(planId: string): Promise<Person[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/people`).then(
      decodePersonList,
    );
  }

  /** The plan's eleven topics, as this reader may see them. */
  listTopics(planId: string): Promise<TopicView[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/topics`).then(
      decodeTopicList,
    );
  }

  /** Opens a topic for the reader: "Start" for the creator, the first visit for anyone else. */
  openTopic(planId: string, key: string): Promise<TopicView> {
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/topics/${encode(key)}/open`,
    ).then(decodeTopicEnvelope);
  }

  /**
   * Opens a suggestion for the plan's creator, starting its topic when needed. An issue
   * suggestion answers with its issue; a topic suggestion answers with `issue: null`.
   */
  async exploreSuggestion(
    planId: string,
    suggestionId: string,
    options?: { from?: 'home' },
  ): Promise<{ issue: string | null; topic: string; thread: string }> {
    const body = await this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/suggestions/${encode(suggestionId)}/explore`,
      options,
    );
    const raw = (body ?? {}) as Record<string, unknown>;
    if (
      (raw['issue'] !== null &&
        raw['issue'] !== undefined &&
        typeof raw['issue'] !== 'string') ||
      typeof raw['topic'] !== 'string' ||
      typeof raw['thread'] !== 'string'
    ) {
      throw new MalformedResponse('expected issue, topic and thread');
    }
    return {
      issue: (raw['issue'] as string | null | undefined) ?? null,
      topic: raw['topic'],
      thread: raw['thread'],
    };
  }

  /** The reader says they have finished their part of the topic. */
  finishTopic(planId: string, key: string): Promise<TopicView> {
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/topics/${encode(key)}/finish`,
    ).then(decodeTopicEnvelope);
  }

  /** The reader opened the review screen. Mor may ask for an address the invitation needs. */
  reviewTopic(planId: string, key: string): Promise<TopicView> {
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/topics/${encode(key)}/review`,
    ).then(decodeTopicEnvelope);
  }

  /** Invites named people, or the family, into a topic. */
  invite(planId: string, key: string, who: Invite): Promise<TopicView> {
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/topics/${encode(key)}/invite`,
      who,
    ).then(decodeTopicEnvelope);
  }

  /** One direct change to the people list or the decision list. A refusal arrives as `ApiFailure` `refused`. */
  changeRecord(planId: string, change: RecordChange): Promise<void> {
    return this.#request('POST', `/v1/plans/${encode(planId)}/record`, change).then(
      decodeRecordResult,
    );
  }

  /** Adds an issue to a topic's list and answers the new issue's id. */
  addIssue(planId: string, topic: string, label: string): Promise<string> {
    const change: RecordChange = { kind: 'add_decision', label, topic };
    return this.#request('POST', `/v1/plans/${encode(planId)}/record`, change).then(
      (body) => {
        const decision = (body as { decision?: { id?: unknown } | null } | null)
          ?.decision;
        const id = decision?.id;
        if (typeof id !== 'string') {
          throw new Error('The server did not name the new issue.');
        }
        return id;
      },
    );
  }

  /** One issue's workspace: its sections, as the reader may see them. */
  readIssue(planId: string, issueId: string): Promise<IssueView> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/issues/${encode(issueId)}`,
    ).then(decodeIssueView);
  }

  /** Every version of every section of one issue, newest first. */
  readIssueHistory(planId: string, issueId: string): Promise<IssueHistoryEntry[]> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/issues/${encode(issueId)}/versions`,
    ).then(decodeIssueHistory);
  }

  /** Opens the reader's own conversation about an issue, when they have none yet. */
  async openIssue(planId: string, issueId: string): Promise<void> {
    await this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/issues/${encode(issueId)}/open`,
    );
  }

  listDecisions(planId: string): Promise<Decision[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/decisions`).then(
      decodeDecisionList,
    );
  }

  listObjectives(planId: string): Promise<Objective[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/objectives`).then(
      decodeObjectiveList,
    );
  }

  listPendingActions(planId: string): Promise<PendingAction[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/pending_actions`).then(
      decodePendingActionList,
    );
  }

  /**
   * A page of a thread, newest last.
   *
   * `before` is only ever a cursor the server handed back. There is no arithmetic on it
   * anywhere in this package — `seq` is a bigint on the server and a decimal string here,
   * and a client that incremented one would be constructing a sequence number.
   */
  readMessages(threadId: string, before: string | null): Promise<MessagePage> {
    const query = before === null ? '' : `?before=${encodeURIComponent(before)}`;
    return this.#request('GET', `/v1/threads/${encode(threadId)}/messages${query}`).then(
      decodeMessagePage,
    );
  }

  /**
   * Post a message. The body is the only thing that crosses.
   *
   * Not `type`, not `authorId`, not `metadata`, not `seq`, not `createdAt`. The API
   * rejects a body with any extra key rather than stripping it, and that is the behaviour
   * this method is written to never discover.
   */
  postMessage(threadId: string, body: string): Promise<Message> {
    return this.#request('POST', `/v1/threads/${encode(threadId)}/messages`, {
      body,
    }).then(decodeMessageEnvelope);
  }

  /**
   * Reports the seq of the newest message this client has actually loaded, as the caller's
   * own read position on this thread. Fire-and-forget from the caller's point of view — see
   * `plan/usePlan.ts`'s `markRead` for the optimistic local patch that does not wait on this
   * resolving before the badge clears.
   */
  markThreadRead(threadId: string, seq: string): Promise<void> {
    return this.#request('POST', `/v1/threads/${encode(threadId)}/read`, { seq }).then(
      () => undefined,
    );
  }

  /**
   * Resolves a confirmation. Who a ratification did not write to (issue #155, D96) rode
   * on this response once; it is on the confirmation's own system message now, which is
   * where a reader can still see it once this call has returned — see `decodeMetadata`'s
   * `notWrittenTo` and `MessageRow.tsx`.
   */
  resolveAction(actionId: string, resolution: string): Promise<PendingAction> {
    return this.#request('POST', `/v1/pending_actions/${encode(actionId)}/resolve`, {
      resolution,
    }).then(decodePendingActionEnvelope);
  }

  redeemInvitation(token: string): Promise<string> {
    return this.#request('POST', '/v1/invitations/redeem', { token }).then(
      decodeRedemption,
    );
  }

  /**
   * The address the invitation was mailed to, so `Invite.tsx` can hand it to the vendor's
   * sign-up as a starting value before anyone has an account, and whether that invitation
   * is for a demo, so the same screen can say so. This is the one request in this client
   * that is expected to go out with no bearer — `authorization()` already returns `{}`
   * whenever `token()` reads null, so nothing here has to know that; it calls `#request`
   * exactly as every authenticated method above it does.
   */
  invitationAddress(token: string): Promise<InvitationAddress> {
    return this.#request('POST', '/v1/invitations/address', { token }).then(
      decodeInvitationAddress,
    );
  }

  /**
   * Every demo scenario this deployment offers. An empty list is an ordinary answer —
   * a scenario directory with nothing in it yet — but demo mode being OFF is not: that
   * is a `404 not_found`, byte for byte what an unknown address gets (`@mor/api`'s
   * `routes.ts`), so an `ApiFailure` reaching `DemoScenarios.tsx` reads as "there is no
   * demo to offer" rather than as a broken request.
   */
  listDemoScenarios(): Promise<DemoScenario[]> {
    return this.#request('GET', '/v1/demo/scenarios').then(decodeDemoScenarioList);
  }

  /** Starts a demo: writes a fresh plan from `scenarioId`, seated as `seatLabel`. */
  startDemo(scenarioId: string, seatLabel: string): Promise<DemoStart> {
    return this.#request('POST', '/v1/demo/scenarios/start', {
      scenarioId,
      seatLabel,
    }).then(decodeDemoStart);
  }

  /** The markdown text of one seat's character sheet. */
  readDemoSheet(scenarioId: string, seatLabel: string): Promise<string> {
    return this.#request(
      'GET',
      `/v1/demo/scenarios/${encode(scenarioId)}/sheets/${encode(seatLabel)}`,
    ).then(decodeDemoSheet);
  }

  /**
   * Which scenario and seat a demo plan was loaded from — read from the server, not from
   * `demo-stash.ts` (deleted), so `PlanScreen.tsx`'s "Character sheet" link and
   * `CharacterSheet.tsx` itself work from a fresh window or a second device.
   */
  readDemoSource(planId: string): Promise<DemoSource> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/demo/source`).then(
      decodeDemoSource,
    );
  }

  /**
   * Deletes a demo plan the caller started. The server refuses everything else — see
   * `@mor/api`'s `routes.ts` for the two conditions — so this method carries no argument
   * beyond the plan id and reports nothing back beyond whether it worked.
   */
  deleteDemoPlan(planId: string): Promise<void> {
    return this.#request('POST', `/v1/plans/${encode(planId)}/demo/delete`).then(
      () => undefined,
    );
  }

  /**
   * Fresh single-use credentials for the speech vendors. POST because every call mints new
   * ones. A 503 arrives as `ApiFailure` `voice_unavailable`: the keys are not set here.
   */
  voiceToken(planId: string): Promise<VoiceTokens> {
    return this.#request('POST', `/v1/plans/${encode(planId)}/voice/session`).then(
      decodeVoiceTokens,
    );
  }

  /**
   * What the browser used of the speech vendors, reported for the operator costs estimate.
   * Sent with keepalive so a report made while the page is closing still arrives.
   */
  async reportVoiceUsage(
    planId: string,
    body:
      | { kind: 'transcription'; model: string; seconds: number }
      | { kind: 'speech'; model: string; characters: number },
  ): Promise<void> {
    await this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/voice/usage`,
      body,
      undefined,
      true,
    );
  }

  /**
   * `release-notes.json`, not a `/v1` route: it is a static file Vite copies into `dist`
   * and the server's static handler serves unauthenticated, same as `index.html`. Going
   * through `#request` anyway rather than a bespoke fetch keeps "nothing outside this
   * module calls fetch" true and gets the same test double every other read uses.
   */
  releaseNotes(): Promise<ReleaseNote[]> {
    return this.#request('GET', '/release-notes.json').then(decodeReleaseNoteList);
  }

  /**
   * The artifact of one topic's decisions, as bytes and a filename.
   *
   * Fetched rather than linked because the route is bearer-authenticated and an `<a href>`
   * carries no header. The filename comes from the server's `Content-Disposition`; the
   * client falls back to a fixed name rather than composing one out of the plan's, because
   * naming the file is subcomponent 10's job and a second copy of it would drift.
   */
  async downloadArtifact(
    planId: string,
    topic: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const headers: Record<string, string> = { ...this.authorization() };
    let response: Response;
    try {
      response = await this.#fetch(
        `${this.baseUrl}/v1/plans/${encode(planId)}/topics/${encode(topic)}/artifact`,
        {
          method: 'GET',
          headers,
        },
      );
    } catch {
      throw new ApiFailure(0, 'unreachable', 'could not reach the server');
    }
    if (!response.ok) {
      let parsed: unknown = {};
      try {
        parsed = JSON.parse(await response.text());
      } catch {
        parsed = {};
      }
      throw failureFrom(response.status, parsed);
    }
    const blob = await response.blob();
    return { blob, filename: filenameFrom(response.headers.get('content-disposition')) };
  }

  decisionNotes(planId: string, decisionId: string): Promise<DecisionNotes> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/decision-notes/${encode(decisionId)}`,
    ).then(decodeDecisionNotes);
  }

  personNotes(planId: string, rosterEntryId: string): Promise<PersonNotes> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/person-notes/${encode(rosterEntryId)}`,
    ).then(decodePersonNotes);
  }

  listFiles(planId: string): Promise<FileView[]> {
    return this.#request('GET', `/v1/plans/${encode(planId)}/files`).then(decodeFileList);
  }

  getFile(planId: string, fileId: string): Promise<FileView> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/files/${encode(fileId)}`,
    ).then(decodeFileEnvelope);
  }

  listTopicFiles(planId: string, topicKey: string): Promise<FileView[]> {
    return this.#request(
      'GET',
      `/v1/plans/${encode(planId)}/topics/${encode(topicKey)}/files`,
    ).then(decodeFileList);
  }

  uploadFile(
    planId: string,
    file: File,
    visibility: 'just_me' | 'principals' | 'everyone',
    originThreadId: string | null,
  ): Promise<FileView> {
    const form = new FormData();
    form.append('file', file);
    form.append('visibility', visibility);
    if (originThreadId !== null) {
      form.append('originThreadId', originThreadId);
    }
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/files`,
      undefined,
      form,
    ).then(decodeFileEnvelope);
  }

  setFileVisibility(
    planId: string,
    fileId: string,
    visibility: 'just_me' | 'principals' | 'everyone',
  ): Promise<FileView> {
    return this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/files/${encode(fileId)}/visibility`,
      { visibility },
    ).then(decodeFileEnvelope);
  }

  async deleteFile(planId: string, fileId: string): Promise<void> {
    await this.#request(
      'POST',
      `/v1/plans/${encode(planId)}/files/${encode(fileId)}/delete`,
    );
  }

  /** Fetches the original with the bearer header, then hands the browser an object URL. */
  async downloadFileOriginal(
    planId: string,
    fileId: string,
    name: string,
  ): Promise<void> {
    let response: Response;
    try {
      response = await this.#fetch(
        `${this.baseUrl}/v1/plans/${encode(planId)}/files/${encode(fileId)}/original`,
        { method: 'GET', headers: { ...this.authorization() } },
      );
    } catch {
      throw new ApiFailure(0, 'unreachable', 'could not reach the server');
    }
    if (!response.ok) {
      let parsed: unknown = {};
      try {
        parsed = JSON.parse(await response.text());
      } catch {
        parsed = {};
      }
      throw failureFrom(response.status, parsed);
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }
}

function encode(id: string): string {
  return encodeURIComponent(id);
}

// The same pattern `packages/api/src/input.ts` validates path identifiers against. A
// `planId` that fails this is not trusted for anything, including navigation: it becomes
// `undefined` and the fallback copy renders, exactly as it does when the field is absent.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

function failureFrom(status: number, parsed: unknown): ApiFailure {
  let code: ApiErrorCode = 'internal';
  let message = 'the request could not be completed';
  // Read the same way `code` and `message` are: a value that is not the type expected is
  // ignored rather than trusted, so a malformed body can never produce a control that
  // navigates somewhere meaningless.
  let planId: string | undefined;
  if (parsed !== null && typeof parsed === 'object') {
    const error = (parsed as Record<string, unknown>)['error'];
    if (error !== null && typeof error === 'object') {
      const rawCode = (error as Record<string, unknown>)['code'];
      if (typeof rawCode === 'string' && ERROR_CODES.has(rawCode)) {
        code = rawCode as ApiErrorCode;
      }
      const rawMessage = (error as Record<string, unknown>)['message'];
      if (typeof rawMessage === 'string') {
        message = rawMessage;
      }
      const rawPlanId = (error as Record<string, unknown>)['planId'];
      if (typeof rawPlanId === 'string' && UUID.test(rawPlanId)) {
        planId = rawPlanId;
      }
    }
  }
  return new ApiFailure(status, code, message, planId);
}

/**
 * The `filename` parameter, or a fixed fallback.
 *
 * Only the ASCII parameter is read. `filename*` is RFC 5987 percent-encoded and decoding
 * it here would be this package's second implementation of a header the API composes; the
 * plain parameter is always present and is always a usable name.
 */
function filenameFrom(header: string | null): string {
  const match = header === null ? null : /filename="([^"]*)"/u.exec(header);
  const name = match?.[1];
  return name === undefined || name === '' ? 'plan.pdf' : name;
}
